import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

interface TextCopyChipProps {
  text: string;
}

const TextCopyChip: React.FC<TextCopyChipProps> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;

    const timeout = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      onClick={(event) => {
        event.stopPropagation();
        void handleCopy();
      }}
      sx={{
        display: "flex",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#f6f8fa",
        border: "1px solid #d1d9e0",
        borderRadius: "6px",
        padding: "4px 6px",
        fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
        fontSize: "0.8rem",
        color: "#24292f",
        cursor: "pointer",
        maxWidth: "100%",
        '&:hover': {
          backgroundColor: '#e6f3ff'
        }
      }}
    >
      <Typography
        component="code"
        sx={{
          fontFamily: "inherit",
          fontSize: "inherit",
          color: "inherit",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: "1em",
          height: "1em",
          position: "relative",
          flexShrink: 0,
        }}
      >
        <ContentCopyIcon
          fontSize="inherit"
          sx={{
            fontSize: "1em",
            position: "absolute",
            opacity: copied ? 0 : 1,
            transition: "opacity 0.5s ease-in-out",
            color: "currentColor",
          }}
        />
        <CheckCircleIcon
          fontSize="inherit"
          sx={{
            fontSize: "1em",
            position: "absolute",
            opacity: copied ? 1 : 0,
            transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
            transform: copied ? "scale(1)" : "scale(0.8)",
            color: "#16a34a",
          }}
        />
      </Stack>
    </Stack>
  );
};

export default TextCopyChip;